import React, { type CSSProperties, useState } from "react";
import { ExternalLinkIcon } from "lucide-react";
import { Link as RouterLink } from "react-router-dom";

import { cn } from "@/components/ui/utils";

import { typographyStyles } from "@/components/ui/typography";

interface LinkProps {
  href: string;
  level?: keyof typeof typographyStyles;
  color?: string;
  isExternal?: boolean;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

export const Link: React.FC<LinkProps> = ({
  href,
  level = "body-lg",
  color,
  children,
  isExternal,
  style,
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const [hoverDirection, setHoverDirection] = useState<"in" | "out" | "none">(
    "none",
  );

  const commonProps = {
    onMouseEnter: () => {
      setIsHovering(true);
      setHoverDirection("in");
    },
    onMouseLeave: () => {
      setIsHovering(false);
      setHoverDirection("out");
    },
    style: {
      ...(typographyStyles as Record<string, React.CSSProperties>)[level],
      color: color
        ? color
        : (typographyStyles as Record<string, React.CSSProperties>)[level]
            .color,
      textDecoration: "none",
      display: "inline-block",
      position: "relative" as const,
      zIndex: 1,
      ...style,
    },
  };

  // A small level's underline a hairline on its foot; a larger's thicker,
  // under it.
  const isSmall =
    level.includes("xs") || level.includes("sm") || level.includes("md");
  const content = (
    <>
      <div
        className={cn(
          "absolute w-0 rounded-[2px] bg-(--link-underline) [transition:width_0.3s]",
          isSmall ? "h-px bottom-0" : "h-0.5 bottom-[-2px]",
          // Drawn from the side the pointer came in at, and taken back
          // towards the side it left by.
          hoverDirection === "in" && "left-0",
          hoverDirection === "out" && "right-0",
          isHovering && "w-full",
        )}
        style={
          {
            "--link-underline": `color-mix(in srgb, ${color ?? (typographyStyles as Record<string, React.CSSProperties>)[level].color} 50%, transparent)`,
          } as CSSProperties
        }
      />
      {children}
      {isExternal && (
        <div className="inline-block relative ml-[0.5em] w-[0.8em] z-2 h-[0.8em] [&_svg]:w-full [&_svg]:h-full">
          <ExternalLinkIcon size="1em" />
        </div>
      )}
    </>
  );

  if (isExternal) {
    return (
      <a {...commonProps} href={href} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    );
  }

  return (
    <RouterLink {...commonProps} to={href}>
      {content}
    </RouterLink>
  );
};
